import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal } from "@/components/ui/Modal";
import { PlayIcon, PauseIcon, MusicNoteIcon } from "@/components/icons";

interface AudioPlayerProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  title: string;
}

/**
 * Format seconds into mm:ss display string.
 */
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/**
 * Modal audio player with HTML5 audio element.
 * Provides play/pause, seek bar, volume control, and duration display.
 */
export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  isOpen,
  onClose,
  src,
  title,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [isOpen]);

  // Sync audio element events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [isOpen, src]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => setIsPlaying(false));
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const audio = audioRef.current;
      const newVolume = parseFloat(e.target.value);
      setVolume(newVolume);
      if (audio) {
        audio.volume = newVolume;
      }
    },
    [],
  );

  const handleClose = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="sm">
      <div className="p-6">
        {/* Album art placeholder */}
        <div className="flex items-center justify-center w-full h-40 bg-gray-100 rounded-lg mb-6">
          <MusicNoteIcon className="w-16 h-16 text-gray-400" />
        </div>

        {/* Hidden audio element */}
        <audio ref={audioRef} src={src} preload="metadata" />

        {/* Play/Pause button */}
        <div className="flex items-center justify-center mb-4">
          <button
            onClick={togglePlayPause}
            className="w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 transition-colors"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <PauseIcon className="w-5 h-5" />
            ) : (
              <PlayIcon className="w-5 h-5 ml-0.5" />
            )}
          </button>
        </div>

        {/* Seek bar */}
        <div className="mb-4">
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-gray-900"
          />
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume control */}
        <div className="flex items-center gap-2">
          <VolumeIndicator volume={volume} />
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={handleVolumeChange}
            className="flex-1 h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-gray-900"
          />
        </div>
      </div>
    </Modal>
  );
};

/**
 * Small volume icon that reflects the current volume level.
 */
function VolumeIndicator({ volume }: { volume: number }) {
  // Using inline SVG here because there is no VolumeIcon in the icon library.
  // This is a simple, non-reusable indicator bound to this component.
  return (
    <svg
      className="w-4 h-4 text-gray-500 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
      {volume > 0 && (
        <path d="M15.54 8.46a5 5 0 010 7.07" />
      )}
      {volume > 0.5 && (
        <path d="M19.07 4.93a10 10 0 010 14.14" />
      )}
    </svg>
  );
}
